// The leagues this account really has, from the server (ICD-S01).
//
// WHY A PROVIDER AND NOT A HOOK THAT FETCHES. Two things read the hub — the
// hub screen and the league switcher in the shell — and they are on screen at
// the same time. A hook that fetched would fetch twice per page and hold two
// loading states that disagree with each other.
//
// WHY IT IS SEPARATE FROM THE SIMULATOR. The sim is not being migrated off;
// it is the tutorial, and it is the example leagues somebody looks around
// before they have their own. What changes is that it stops being the ONLY
// answer. `useHub()` merges the two, real leagues first.
//
// THE READING ITSELF MOVED OUT on 2026-09-17. What is left here is the two
// things that are genuinely about the hub rather than about reading: the
// session gate below, and what to do when the answer is 401. Everything else
// — `loading` starting true, the answer being keyed to whose it is, the abort,
// the parse — is `data/read.ts` now, and the comment that used to explain each
// of them is there, because four other readers needed the same rules.

import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { HubView, isLoading, valueOf, type LeagueCard, type Snapshot } from '@fsp/domain';
import { useSession } from './session';
import { useRead, type Read } from './read';
import { leagueIdFromPath } from './route';
import { isDemoLeague, useDemoCard } from './demo';
import { useTutorialLeague } from './SimProvider';

const LeaguesContext = createContext<Read<HubView | null> | null>(null);

export function LeaguesProvider({ children }: { children: ReactNode }) {
  const { session, reconciled, forget } = useSession();
  const userId = session?.userId ?? null;

  // Signed out is not a failure and not a load: it is an answer, and it is
  // the empty one. Asking the server would just be a 401 we already know.
  //
  // `pending` IS THE OTHER HALF, and without it the empty answer is a lie for
  // one render. A cold load with a valid cookie and nothing in localStorage —
  // a second device, cleared site data, the link a friend sent — has no
  // `userId` until `GET /api/me` answers, and "signed in, asked, in no
  // leagues" is a sentence route guards act on. It bounced somebody from
  // `/l/<id>` to the hub, with the league's own card visible on the page they
  // landed on. So: no session AND not yet reconciled means we do not know.
  //
  // `key` is the id because `/api/hub` is the same URL for everybody. The
  // cookie decides whose leagues come back, so without it, signing into a
  // second account in the same tab would show the first one's.
  const read = useRead(userId === null ? null : '/hub', HubView, {
    key: userId ?? '',
    pending: userId === null && !reconciled,
  });

  // The session went while we were asking. Nothing here can renew it, and a
  // browser that keeps a cached identity for a session the server has
  // forgotten shows a name in the header bar for an account it can no longer
  // act as. `useSession` would find out on its own eventually; this is the
  // request that already knows.
  const failed = read.snapshot.state === 'failed' ? read.snapshot.failure.code : null;
  useEffect(() => {
    if (failed === 'unauthorized') forget();
  }, [failed, forget]);

  return <LeaguesContext.Provider value={read}>{children}</LeaguesContext.Provider>;
}

export function useLeagues(): Read<HubView | null> {
  const ctx = useContext(LeaguesContext);
  if (!ctx) throw new Error('useLeagues must be used inside LeaguesProvider');
  return ctx;
}

/**
 * The hub as the shell reads it: the cards, and whether we are still finding
 * out.
 *
 * A convenience over the snapshot rather than a replacement for it — three
 * call sites want exactly these two things, and a fourth (the chore strip)
 * needs the snapshot itself, because an empty chore list and a chore list
 * nobody computed are different answers.
 */
export function useLeagueCards(): { cards: LeagueCard[]; loading: boolean } {
  const { snapshot } = useLeagues();
  return useMemo(
    // `valueOf`, not `snapshot.value`: a refresh that failed keeps the cards
    // it already had. Blanking the hub because one poll did not land is the
    // thing `lastValue` exists to prevent.
    () => ({ cards: valueOf(snapshot)?.leagues ?? [], loading: isLoading(snapshot) }),
    [snapshot],
  );
}

/** One league card by id, or null — the real card behind `/l/:leagueId`. */
export function cardFor(snapshot: Snapshot<HubView | null>, leagueId: string | null) {
  if (leagueId === null) return null;
  return valueOf(snapshot)?.leagues.find((c) => c.league.id === leagueId) ?? null;
}

/**
 * The real league card for the route we are on, or null.
 *
 * WHAT IT IS FOR. The app shell asked the SIMULATOR what league it was
 * showing — its name for the phone menu's title, `status === 'setup'` for
 * which tabs to draw, `isCommissioner` for whether to offer Settings. For a
 * real league those are three answers about somebody else's world. It
 * happened to be harmless only because a real league id is usually absent
 * from the simulated world, so the shell fell back to hub chrome; where the
 * ids collide — `db:seed:local` generated the fixture FROM the simulator, so
 * `family` is both Robin's real league and the practice one — the shell drew
 * Ken's practice league's title and Ken's commissioner buttons over Robin's
 * real league.
 *
 * Null while the hub is still loading, which is the honest answer and the one
 * the shell wants: it draws hub chrome until it knows.
 */
export function useActiveLeague(): LeagueCard | null {
  const { snapshot } = useLeagues();
  const { pathname } = useLocation();
  const leagueId = leagueIdFromPath(pathname);
  // THE DEMO LEAGUE IS NOBODY'S, so no hub lists it: its card is the one
  // `GET /api/demo` answers, as the seat a visitor sits in (O6).
  const demo = useDemoCard();
  // THE TUTORIAL'S LEAGUES ARE THE SIMULATOR'S (B-527), whatever the hub holds
  // under the same id.
  const tutorial = useTutorialLeague(leagueId);
  return useMemo(
    () => (tutorial ? null : isDemoLeague(leagueId) ? demo : cardFor(snapshot, leagueId)),
    [snapshot, leagueId, demo, tutorial],
  );
}
